import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import * as React from "react"
import { PageHeader } from "@/components/layout"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { ConfigEditor } from "@/features/discord-bot-admin/ConfigEditor"
import { InvocationsGrid } from "@/features/discord-bot-admin/InvocationsGrid"
import { MemoriesPanel } from "@/features/discord-bot-admin/MemoriesPanel"
import { UsagePanel } from "@/features/discord-bot-admin/UsagePanel"
import { createTabSearch } from "@/hooks/use-url-tab"
import { useDiscordBotConfig } from "@/queries/marketplace/use-discord-bot-config"

const TABS = ["invocations", "config", "memories", "usage"] as const
type BotTab = (typeof TABS)[number]

export const Route = createFileRoute("/discord-bot/")({
	component: DiscordBotPage,
	validateSearch: createTabSearch(TABS),
})

function DiscordBotPage() {
	const navigate = useNavigate()
	const { hasAnyRole, hasRole } = useAdminRolesContext()
	const canRead = hasAnyRole([AdminRole.DiscordRead, AdminRole.DiscordAdmin])
	const canEdit = hasRole(AdminRole.DiscordAdmin)
	const { configVersion, isLoading } = useDiscordBotConfig()
	const searchParams = Route.useSearch()
	const activeTab = searchParams.tab ?? "invocations"

	const setActiveTab = (tab: string) => {
		navigate({
			to: "/discord-bot",
			search: tab === "invocations" ? {} : { tab: tab as BotTab },
			replace: true,
		})
	}

	React.useEffect(() => {
		if (!canRead) navigate({ to: "/unauthorized" })
	}, [canRead, navigate])
	if (!canRead) return null

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-4 p-6">
			<PageHeader
				title="Discord bot"
				description="Invocation history, live configuration and spend"
			/>
			<Tabs
				value={activeTab}
				onValueChange={setActiveTab}
				className="flex flex-1 min-h-0 flex-col"
			>
				<TabsList variant="line">
					<TabsTrigger value="invocations">Invocations</TabsTrigger>
					<TabsTrigger value="config">Configuration</TabsTrigger>
					<TabsTrigger value="memories">Memories</TabsTrigger>
					<TabsTrigger value="usage">Usage & cost</TabsTrigger>
				</TabsList>
				<TabsContent value="invocations" className="mt-4 flex-1 min-h-0">
					<InvocationsGrid />
				</TabsContent>
				<TabsContent
					value="config"
					className="mt-4 flex-1 min-h-0 overflow-y-auto"
				>
					{isLoading ? (
						<p className="text-muted-foreground">Loading…</p>
					) : configVersion ? (
						<ConfigEditor active={configVersion} canEdit={canEdit} />
					) : (
						<p className="text-muted-foreground">
							No config row yet. Seed one from the bot's repo with{" "}
							<code className="font-mono text-xs">pnpm seed:config</code>.
						</p>
					)}
				</TabsContent>
				<TabsContent
					value="memories"
					className="mt-4 flex-1 min-h-0 overflow-y-auto"
				>
					<MemoriesPanel canEdit={canEdit} />
				</TabsContent>
				<TabsContent value="usage" className="mt-4 flex-1 min-h-0">
					<UsagePanel />
				</TabsContent>
			</Tabs>
		</div>
	)
}
